import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { reconcileCompletedCaseStatusWithStepProgress } from "../components/MainContent/pages/Cases/CaseDetail/hooks/caseActionEligibility";
import {
  casesService,
  mapCaseDetailsToCaseDetailData,
  type CaseDetailData,
  type CaseTagItem,
  type CaseNoteItem,
  type CaseActivityItem,
} from "../services/cases.service";

export interface CaseDetailState {
  caseDetail: CaseDetailData | null;
  caseTags: CaseTagItem[];
  caseNotes: CaseNoteItem[];
  caseActivity: CaseActivityItem[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: CaseDetailState = {
  caseDetail: null,
  caseTags: [],
  caseNotes: [],
  caseActivity: [],
  isLoading: false,
  isRefreshing: false,
  error: null,
};

export type FetchCaseDetailArg = string | { caseId: string; soft?: boolean };

export function normalizeFetchCaseDetailArg(arg: FetchCaseDetailArg): {
  caseId: string;
  soft: boolean;
} {
  if (typeof arg === "string") return { caseId: arg, soft: false };
  return {
    caseId: arg.caseId,
    soft: arg.soft === true,
  };
}

function safeTagName(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "detail" in value) {
    const d = (value as { detail?: unknown }).detail;
    return d !== null && d !== undefined ? String(d) : "—";
  }
  return "—";
}

export const fetchCaseDetail = createAsyncThunk(
  "caseDetail/fetchCaseDetail",
  async (arg: FetchCaseDetailArg, { rejectWithValue }) => {
    const { caseId, soft } = normalizeFetchCaseDetailArg(arg);
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id || !caseId) {
      return rejectWithValue("Session or case id not available");
    }
    const res = await casesService.getCaseDetails({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    if (res.success === true && res.data?.case_details) {
      const details = res.data.case_details as Record<string, unknown>;
      const mapped = mapCaseDetailsToCaseDetailData(details, caseId);
      if (soft) return mapped;
      return await reconcileCompletedCaseStatusWithStepProgress(mapped, {
        business_id,
        device_token,
        session_id,
      });
    }
    return rejectWithValue((res.errors as string) ?? "Could not load case details.");
  }
);

export const fetchCaseTags = createAsyncThunk(
  "caseDetail/fetchCaseTags",
  async (caseId: string, { rejectWithValue }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id || !caseId) {
      return rejectWithValue("Session or case id not available");
    }
    const res = await casesService.getCaseTags({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    if (res.success && res.data?.case_tags && Array.isArray(res.data.case_tags)) {
      return res.data.case_tags.map((t) => ({
        ...t,
        name:
          typeof t.tag_name === "string"
            ? t.tag_name
            : typeof (t as { name?: string }).name === "string"
              ? (t as { name: string }).name
              : safeTagName((t as { name?: unknown }).name ?? t.tag_name),
      })) as CaseTagItem[];
    }
    return [];
  }
);

export const fetchCaseNotes = createAsyncThunk(
  "caseDetail/fetchCaseNotes",
  async (caseId: string, { rejectWithValue }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id || !caseId) {
      return rejectWithValue("Session or case id not available");
    }
    const res = await casesService.getCaseNotes({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    if (res.success && res.data?.case_notes && Array.isArray(res.data.case_notes)) {
      return res.data.case_notes;
    }
    return [];
  }
);

export const fetchCaseActivity = createAsyncThunk(
  "caseDetail/fetchCaseActivity",
  async (caseId: string) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id || !caseId) {
      return [];
    }
    const res = await casesService.getCaseActivity({
      case_id: caseId,
      business_id,
      device_token,
      session_id,
    });
    if (res.success && res.data?.case_activity && Array.isArray(res.data.case_activity)) {
      return res.data.case_activity;
    }
    return [];
  }
);

const caseDetailSlice = createSlice({
  name: "caseDetail",
  initialState,
  reducers: {
    setCaseDetail: (state, action: PayloadAction<CaseDetailData | null>) => {
      state.caseDetail = action.payload;
      state.error = null;
    },
    setCaseTags: (state, action: PayloadAction<CaseTagItem[]>) => {
      state.caseTags = action.payload;
    },
    setCaseNotes: (state, action: PayloadAction<CaseNoteItem[]>) => {
      state.caseNotes = action.payload;
    },
    setCaseActivity: (state, action: PayloadAction<CaseActivityItem[]>) => {
      state.caseActivity = action.payload;
    },
    clearCaseDetailError: (state) => {
      state.error = null;
    },
    clearCaseDetail: () => initialState,
    patchCaseDetailFields: (
      state,
      action: PayloadAction<{ caseId: string | number; patch: Partial<CaseDetailData> }>
    ) => {
      const detail = state.caseDetail;
      if (!detail || String(detail.case_id ?? "") !== String(action.payload.caseId)) {
        return;
      }
      state.caseDetail = { ...detail, ...action.payload.patch };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseDetail.pending, (state, action) => {
        const { caseId, soft } = normalizeFetchCaseDetailArg(action.meta.arg as FetchCaseDetailArg);
        state.error = null;
        if (soft) {
          return;
        }
        const existingCaseId = state.caseDetail?.case_id;
        const isSameCase =
          existingCaseId !== undefined &&
          existingCaseId !== null &&
          String(existingCaseId) === String(caseId);
        if (isSameCase) {
          state.isRefreshing = true;
          return;
        }
        if (
          existingCaseId !== undefined &&
          existingCaseId !== null &&
          String(existingCaseId) !== String(caseId)
        ) {
          state.caseDetail = null;
        }
        state.isLoading = true;
      })
      .addCase(fetchCaseDetail.fulfilled, (state, action) => {
        state.caseDetail = action.payload;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(fetchCaseDetail.rejected, (state, action) => {
        const { soft } = normalizeFetchCaseDetailArg(action.meta.arg as FetchCaseDetailArg);
        state.isLoading = false;
        state.isRefreshing = false;
        if (soft) {
          return;
        }
        state.caseDetail = null;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load case details.";
      })
      .addCase(fetchCaseTags.fulfilled, (state, action) => {
        state.caseTags = action.payload;
      })
      .addCase(fetchCaseNotes.fulfilled, (state, action) => {
        state.caseNotes = action.payload;
      })
      .addCase(fetchCaseActivity.fulfilled, (state, action) => {
        state.caseActivity = action.payload;
      });
  },
});

export const {
  setCaseDetail,
  setCaseTags,
  setCaseNotes,
  setCaseActivity,
  clearCaseDetailError,
  clearCaseDetail,
  patchCaseDetailFields,
} = caseDetailSlice.actions;

export const selectCaseDetail = (state: { caseDetail: CaseDetailState }): CaseDetailData | null =>
  state.caseDetail?.caseDetail ?? null;
export const selectCaseTags = (state: { caseDetail: CaseDetailState }): CaseTagItem[] =>
  state.caseDetail?.caseTags ?? [];
export const selectCaseNotes = (state: { caseDetail: CaseDetailState }): CaseNoteItem[] =>
  state.caseDetail?.caseNotes ?? [];
export const selectCaseActivity = (state: { caseDetail: CaseDetailState }): CaseActivityItem[] =>
  state.caseDetail?.caseActivity ?? [];
export const selectCaseDetailLoading = (state: { caseDetail: CaseDetailState }) =>
  state.caseDetail?.isLoading ?? false;
export const selectCaseDetailError = (state: { caseDetail: CaseDetailState }) =>
  state.caseDetail?.error ?? null;

export default caseDetailSlice.reducer;
